import { CdkDragDrop, CdkDragMove } from "@angular/cdk/drag-drop";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { BehaviorSubject, Subject } from "rxjs";
import { tap, takeUntil } from "rxjs/operators";
import {
  PopupFieldData,
  StageDefaultInfo,
} from "src/app/features/designer-studio/edit-pdf-doc/models/doc.model";
import { SetSelectedField } from "src/app/features/designer-studio/edit-pdf-doc/state/actions/doc.actions";
import {
  getPopupStageData,
  getSelectedFieldId,
} from "src/app/features/designer-studio/edit-pdf-doc/state/selectors/doc.selectors";
import { ComponentType } from "../../model/Component.model";
import { ScreenService } from "../../services/screen/screen.service";
import { componentDefaults } from "../componentDefaults";

type Point = {
  id: string;
  left: number;
  top: number;
  width: number;
  type: string;
  height: number;
  bg?: string;
};

@Component({
  selector: "app-stage-manager",
  templateUrl: "./stage-manager.component.html",
  styleUrls: ["./stage-manager.component.scss"],
})
export class StageManagerComponent implements OnInit, OnDestroy {
  stageComponents: PopupFieldData[] = [];
  viewData = null;
  stageInfo: StageDefaultInfo = null;
  componentOptions = new BehaviorSubject([]);
  ComponentType = ComponentType;

  isDeleteComponentModalShown = false;
  stageSelected = true;
  activeComponentId = "";
  removeIsDisabled = true;

  // Resize-related properties
  private isResizing = false;
  private resizeDirection = "";
  private startMousePos = { x: 0, y: 0 };
  private startComponentSize = { width: 0, height: 0 };
  private startComponentPos = { left: 0, top: 0 };
  private currentResizeComponentId = "";
  private minComponentSize = { width: 20, height: 20 };
  destroy$ = new Subject();
  @ViewChild("stageContainer") stageContainer: ElementRef;

  constructor(private screenService: ScreenService, private store: Store) {}

  ngOnInit(): void {
    this.componentOptions = this.screenService.menuData$;
    this.viewData = this.screenService.getScreenData().pipe(
      takeUntil(this.destroy$),
      tap((view) => {
        this.stageComponents = view.popupDetails.data;
      })
    );

    this.store
      .pipe(takeUntil(this.destroy$), select(getPopupStageData))
      .subscribe((stageData) => {
        this.stageInfo = stageData;
      });

    this.store
      .pipe(takeUntil(this.destroy$), select(getSelectedFieldId))
      .subscribe((id) => {
        this.activeComponentId = id;
        if (id) {
          this.stageSelected = false;
        }
      });

    // Listen for changes to manually update stage components after initial load
    this.screenService.componentDataChange$.subscribe((changes) => {
      const fieldIndex = this.stageComponents.findIndex(
        (f) => f.id === changes.id
      );
      if (fieldIndex != -1) {
        this.stageComponents[fieldIndex] = structuredClone(changes);
      }
    });
  }

  ngAfterViewInit() {
    if (this.stageContainer) {
      this.screenService.setStageSize(
        this.stageContainer.nativeElement.getBoundingClientRect()
      );
    }
  }

  stageSelectedHandler() {
    this.stageSelected = true;
    this.removeIsDisabled = true;
    this.store.dispatch(new SetSelectedField(null));
    this.activeComponentId = null;
  }

  componentSelectedHandler(event: MouseEvent, id: string) {
    // Don't select component if we're resizing
    if (this.isResizing) return;

    this.removeIsDisabled = false;
    event.stopPropagation();
    this.store.dispatch(new SetSelectedField(id));
  }

  repositionHandler(event: CdkDragDrop<HTMLElement, any>) {
    const stageClone = structuredClone(this.stageComponents);
    const targetContainer = event.container.element.nativeElement;
    const newComponent = stageClone.find(
      (p) => p.id === event.item.element.nativeElement.id
    );

    newComponent.left += event.distance.x;
    newComponent.top += event.distance.y;
    const newCompData = this.adjustToInBounds(newComponent, targetContainer);

    this.screenService.updateFieldProperties(newCompData, true);
  }

  dragStartHandler(event) {
    event.dataTransfer.clearData();
    const componentList = this.componentOptions.getValue();
    const eventData = componentList.find((c) => c.type === event.target.id);

    if (!eventData) {
      console.warn("Could not find component data");
      return;
    }
    event.dataTransfer.setData("text/plain", JSON.stringify(eventData));
  }

  dragOverHandler(event) {
    event.preventDefault();
  }

  transferHandler(event) {
    event.preventDefault();
    const { target, clientX, clientY } = event;
    const data = event.dataTransfer.getData("text");

    if (!data) {
      console.warn("No data found for component drop");
      return;
    }

    const newComponentData = JSON.parse(data);
    const defaultComponentSettings = componentDefaults[newComponentData.type]
      ? componentDefaults[newComponentData.type]
      : {};

    const newComponent = {
      ...newComponentData,
      ...defaultComponentSettings,
      ...this.getRelativePosition(clientX, clientY, target),
    };

    const adjustedToInBounds = this.adjustToInBounds(newComponent, target);

    adjustedToInBounds.fieldDisplayName =
      "element_" + Math.random().toString(36).slice(-6);
    adjustedToInBounds.id = "_" + Date.now();

    this.addComponent(adjustedToInBounds);
    this.removeIsDisabled = false;
    this.store.dispatch(new SetSelectedField(adjustedToInBounds.id));
  }

  addComponent(point: Point): void {
    this.screenService.addScreenComponent(point);
  }

  toggleDeleteComponentModal() {
    this.isDeleteComponentModalShown = !this.isDeleteComponentModalShown;
  }

  deleteFieldHandler(closeEvent: "Delete" | "Cancel") {
    if (this.activeComponentId && closeEvent === "Delete") {
      let componentClone = structuredClone(this.stageComponents);
      componentClone = componentClone.filter(
        (c) => c.id !== this.activeComponentId
      );

      this.stageComponents = componentClone;

      this.screenService.removeFieldFromStore(this.activeComponentId);
      this.activeComponentId = "";
      this.stageSelected = true;
      this.removeIsDisabled = true;
    }

    this.isDeleteComponentModalShown = false;
  }

  private adjustToInBounds(component: PopupFieldData, parent: HTMLElement) {
    const componentClone = structuredClone(component);

    const parentRect = parent.getBoundingClientRect();
    const parentHeight = parentRect.height;
    const parentWidth = parentRect.width;
    const compBottom = componentClone.top + componentClone.height;
    const compRight = componentClone.left + componentClone.width;

    if (compBottom > parentHeight)
      componentClone.top = parentHeight - componentClone.height;
    if (compRight > parentWidth)
      componentClone.left = parentWidth - componentClone.width;
    if (componentClone.left < 0) componentClone.left = 0;
    if (componentClone.top < 0) componentClone.top = 0;

    componentClone.left = Math.floor(componentClone.left);
    componentClone.top = Math.floor(componentClone.top);

    return componentClone;
  }

  getRelativePosition(clientX: number, clientY: number, element: HTMLElement) {
    const rect = element.getBoundingClientRect();

    return {
      left: Math.floor(clientX - rect.left),
      top: Math.floor(clientY - rect.top),
    };
  }

  dragged(ev: CdkDragMove) {
    const elRect = ev.source.element.nativeElement.getBoundingClientRect();
    const comp = this.stageComponents.find(
      (field) => field.id === this.activeComponentId
    );
    const parentRect =
      ev.source.dropContainer.element.nativeElement.getBoundingClientRect();

    const elRightPos = comp.left + elRect.width + parentRect.x;
    const elBottomPos = comp.top + elRect.height + parentRect.y;

    if (
      elRightPos + ev.distance.x >= parentRect.right ||
      elBottomPos + ev.distance.y >= parentRect.bottom
    ) {
      this.screenService.dragWarning$.next(true);
    } else {
      this.screenService.dragWarning$.next(false);
    }
  }

  // Resize functionality
  startResize(event: MouseEvent, componentId: string, direction: string) {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.resizeDirection = direction;
    this.currentResizeComponentId = componentId;

    // Store initial mouse position
    this.startMousePos = { x: event.clientX, y: event.clientY };

    // Store initial component properties
    const component = this.stageComponents.find((c) => c.id === componentId);
    if (component) {
      this.startComponentSize = {
        width: component.width,
        height: component.height,
      };
      this.startComponentPos = { left: component.left, top: component.top };
    }

    // Add global mouse event listeners
    document.addEventListener("mousemove", this.onResize.bind(this));
    document.addEventListener("mouseup", this.stopResize.bind(this));

    // Prevent text selection during resize
    document.body.style.userSelect = "none";
  }

  private onResize(event: MouseEvent) {
    if (!this.isResizing) return;

    const component = this.stageComponents.find(
      (c) => c.id === this.currentResizeComponentId
    );
    if (!component) return;

    const deltaX = event.clientX - this.startMousePos.x;
    const deltaY = event.clientY - this.startMousePos.y;

    let newWidth = this.startComponentSize.width;
    let newHeight = this.startComponentSize.height;
    let newLeft = this.startComponentPos.left;
    let newTop = this.startComponentPos.top;

    // Calculate new dimensions based on resize direction
    switch (this.resizeDirection) {
      case "se": // Southeast (bottom-right)
        newWidth = Math.max(
          this.minComponentSize.width,
          this.startComponentSize.width + deltaX
        );
        newHeight = Math.max(
          this.minComponentSize.height,
          this.startComponentSize.height + deltaY
        );
        break;
      case "sw": // Southwest (bottom-left)
        newWidth = Math.max(
          this.minComponentSize.width,
          this.startComponentSize.width - deltaX
        );
        newHeight = Math.max(
          this.minComponentSize.height,
          this.startComponentSize.height + deltaY
        );
        newLeft =
          this.startComponentPos.left +
          (this.startComponentSize.width - newWidth);
        break;
      case "ne": // Northeast (top-right)
        newWidth = Math.max(
          this.minComponentSize.width,
          this.startComponentSize.width + deltaX
        );
        newHeight = Math.max(
          this.minComponentSize.height,
          this.startComponentSize.height - deltaY
        );
        newTop =
          this.startComponentPos.top +
          (this.startComponentSize.height - newHeight);
        break;
      case "nw": // Northwest (top-left)
        newWidth = Math.max(
          this.minComponentSize.width,
          this.startComponentSize.width - deltaX
        );
        newHeight = Math.max(
          this.minComponentSize.height,
          this.startComponentSize.height - deltaY
        );
        newLeft =
          this.startComponentPos.left +
          (this.startComponentSize.width - newWidth);
        newTop =
          this.startComponentPos.top +
          (this.startComponentSize.height - newHeight);
        break;
      case "n": // North (top)
        newHeight = Math.max(
          this.minComponentSize.height,
          this.startComponentSize.height - deltaY
        );
        newTop =
          this.startComponentPos.top +
          (this.startComponentSize.height - newHeight);
        break;
      case "s": // South (bottom)
        newHeight = Math.max(
          this.minComponentSize.height,
          this.startComponentSize.height + deltaY
        );
        break;
      case "e": // East (right)
        newWidth = Math.max(
          this.minComponentSize.width,
          this.startComponentSize.width + deltaX
        );
        break;
      case "w": // West (left)
        newWidth = Math.max(
          this.minComponentSize.width,
          this.startComponentSize.width - deltaX
        );
        newLeft =
          this.startComponentPos.left +
          (this.startComponentSize.width - newWidth);
        break;
    }

    // Ensure component stays within stage bounds
    const stageElement = document.querySelector(".stage") as HTMLElement;
    if (stageElement) {
      const stageRect = stageElement.getBoundingClientRect();
      const maxWidth = stageRect.width - newLeft;
      const maxHeight = stageRect.height - newTop;

      newWidth = Math.min(newWidth, maxWidth);
      newHeight = Math.min(newHeight, maxHeight);
      newLeft = Math.max(0, Math.min(newLeft, stageRect.width - newWidth));
      newTop = Math.max(0, Math.min(newTop, stageRect.height - newHeight));
    }

    // Update component properties
    component.width = Math.round(newWidth);
    component.height = Math.round(newHeight);
    component.left = Math.round(newLeft);
    component.top = Math.round(newTop);

    // Update the component in the service
    this.screenService.updateFieldProperties(component, true);
  }

  private stopResize(event: MouseEvent) {
    if (!this.isResizing) return;

    this.isResizing = false;
    this.resizeDirection = "";
    this.currentResizeComponentId = "";

    // Remove global event listeners
    document.removeEventListener("mousemove", this.onResize.bind(this));
    document.removeEventListener("mouseup", this.stopResize.bind(this));

    // Restore text selection
    document.body.style.userSelect = "";

    // Final update to save the changes
    const component = this.stageComponents.find(
      (c) => c.id === this.currentResizeComponentId
    );
    if (component) {
      this.screenService.updateFieldProperties(component, true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
